/**
 * FP 현황 분석 첫 화면(현황판) — [D-109] · [Q-150] 임원 보고 피드백 「첫 화면 구성이 빈약하다」.
 *
 * ★ 무엇을 채웠나 (생산계획이 실제로 가진 결과 테이블 기준 — 타 모듈을 베끼지 않는다)
 *   ① KPI 카드 5장에 **색(판정)과 비교 기준 대비 ▲▼** — 비교 기준은 직전 계획 / 최근 확정 계획
 *   ② 납기 현황(준수·지연·결품) · 리드타임 구성(공정 내 체류·공정 간 대기) 분포 막대
 *   ③ 주요 이슈 카드 — 결과 테이블에 고정 규칙을 적용한 신호(결품·지연·병목·능력 여유·악화)
 *   ④ 문제 항목 TOP 5 — 병목 자원(포화 비율) · 품목별 문제 주문(준수·지연·결품 누적 막대)
 *   ⑤ 기간별 자원 부하 추이 — 부하 vs 여유 능력(일/주/월 · 공정 범위) + 가동률 구간별 자원 수
 *
 * ★ 판정은 서버가 한다 — `saas-ai-agent/src/scenario/fp/status_board.py` 가 톤·방향·심각도를 준다.
 *   이 파일은 그것을 색으로 옮길 뿐 **두 번째 판정 체계를 만들지 않는다**(값 색 · 배지 색 · 병목 표시 ·
 *   신호 심각도 전부 서버 필드를 읽는다). 유일한 예외는 **표시 구간**(가동률 구간별 자원 수의 구간
 *   경계)인데, 그것은 분포를 보여 주는 눈금이지 좋고 나쁨의 판정이 아니다.
 *
 * ★ 순환 import 금지 — 이 파일은 AiFpScenarioPlanning.js 를 import 하지 않는다(그 파일이 이 파일을
 *   import 한다). KPI 라벨처럼 모듈 객체에 있는 값은 인자로 받는다.
 */
import React, { useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  C as UIC, BAR_TIP_PROPS, BarTip, ForecastActualChart,
  STATUS_BAR_COL_SX, STATUS_BAR_H, STATUS_BAR_RADIUS, STATUS_BAR_TRACK,
  STATUS_CARD_GRID_SX, STATUS_TARGET_CHIP_SX,
  StatusCardField, StatusEmpty, StatusInsightCard, StatusItemHeader, StatusItemList, StatusItemRow,
  StatusSection, StatusToolbar, StatusToolbarSelect,
} from "./scenarioStatusUi";
import AiSegmentedControl from "./AiSegmentedControl";
import { cellText } from "./aiFieldText";
import { FONT } from "@plannel/components/ai/typographyTokens";

// ── 색 ────────────────────────────────────────────────────────────────────────
/** 서버 톤 → 색. neutral 은 종전 FP 카드 색(파랑)이다 — 판정이 없는 축은 종전 모습 그대로다. */
export const FP_TONE_COLOR = { good: UIC.g1, warn: UIC.g2, bad: UIC.g3, neutral: UIC.acc };
/** 증감 배지 색 — 변화 없음/비교 불가는 회색이다(파랑은 「값」의 중립색이라 배지에 쓰면 판정처럼 읽힌다). */
const DELTA_TONE_COLOR = { good: UIC.g1, warn: UIC.g2, bad: UIC.g3, neutral: UIC.text3 };

// severity → 색·라벨. 공용 컨테이너의 비교 탭 매핑(실현가능성 경고)과 **같은 색**이다 — 같은 severity 가
// 화면마다 다른 색이면 사용자가 두 화면을 잇지 못한다. 라벨 키 3종은 이미 존재한다.
export const FP_SEVERITY = {
  HIGH:   { color: UIC.g3,  text: UIC.g3t, labelKey: "urgent" },
  MEDIUM: { color: UIC.g2,  text: UIC.g2t, labelKey: "alert"  },
  LOW:    { color: UIC.acc, text: UIC.accT, labelKey: "verify" },
};
export const FP_SEVERITY_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };

// 납기 분류 색 — 준수 / 지연 / 결품(계획 실패). MP 현황 탭 OTD 추이(g1 · g2s · g3)와 같은 계열이다.
const DELIVERY_COLOR = { ontime: UIC.g1, late: UIC.g2s, short: UIC.g3 };
const FLOW_COLOR = { g: UIC.accM, i: UIC.g2s };

// ── 숫자 ──────────────────────────────────────────────────────────────────────
export const fpFmt = (v, digits = 1) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });

/** 소수 자릿수 **고정** — 같은 열·같은 줄에 놓이는 비율·일수용. `fpFmt` 는 끝의 0 을 지워 「68」 옆에 「92.7」 이 선다(캡처 169). */
export const fpFix = (v, digits = 1) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const pctOf = (n, d) => (d > 0 ? (n / d) * 100 : null);

// 축 → 표시 단위 부류. 서버 FP_KPI_META 의 단위와 같다(%·일·분·회).
const AXIS_KIND = { a: "pct", b: "pct", c: "pct", h: "pct", g: "day", i: "day", d: "day", e: "min", f: "times" };
const AXIS_DIGITS = { pct: 1, day: 1, min: 0, times: 0 };
const digitsOf = (key) => AXIS_DIGITS[AXIS_KIND[key]] ?? 1;
const unitOf = (t, key) => ({
  pct: t("percentUnit"), day: t("fpAiUnitDay"), min: t("fpAiUnitMinute"), times: t("unitTimes"),
}[AXIS_KIND[key]] ?? "");

/** 축 값 숫자부 — 단위 부류별 고정 자릿수. */
export const fpAxisNumText = (key, v) => fpFix(v, digitsOf(key));

/** 비교 기준 값 표시(단위 포함) — 서버가 기준 값의 표시 문자열은 주지 않으므로 여기서 조립한다. */
export function fpAxisValueText(t, key, v) {
  if (v == null) return "—";
  return `${fpAxisNumText(key, v)}${unitOf(t, key)}`;
}

/** 증감 텍스트 — 비율 축은 %p, 나머지는 축 단위. 부호는 수치 방향(좋고 나쁨은 배지 색·라벨이 말한다). */
export function fpDeltaText(t, key, delta) {
  if (delta == null) return null;
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : "";
  const abs = Math.abs(delta);
  if (AXIS_KIND[key] === "pct") return `${sign}${fpFix(abs, 1)}${t("fpSigUnitPctPoint")}`;
  return `${sign}${fpAxisNumText(key, abs)}${unitOf(t, key)}`;
}

/**
 * 가용폭(px) 관측 — 열 수를 **뷰포트가 아니라 실제 폭**으로 고른다. 사이드 메뉴·대화창이 열리면 같은
 * 뷰포트에서도 폭이 절반이 된다(캡처 172·173 — 650px 창에서 카드 3열이 값을 잘라 먹었다).
 * 콜백 ref 다 — 빈 상태로 먼저 그렸다가 나중에 붙는 요소도 관측한다. 폭을 모르면 0(→ 호출부 기본값).
 */
export function useFpElementWidth() {
  const [el, setEl] = useState(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!el) return undefined;
    setWidth(el.clientWidth || 0);
    if (typeof ResizeObserver === "undefined") return undefined;
    let raf = 0;
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[entries.length - 1].contentRect.width);
      // 다음 프레임으로 미룬다 — 열 수가 바뀌며 높이가 변하고 스크롤바가 오가면 같은 주기에서 되먹임이 난다.
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWidth((p) => (p === w ? p : w)));
    });
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, [el]);
  return [setEl, width];
}

/** KPI 카드 열 수 — 카드 1장에 값 + 단위 + 증감 배지가 들어갈 폭(약 230px)을 기준으로 5 · 3 · 2 · 1. 폭 미상이면 3. */
export const fpKpiColumns = (w) => (!w ? 3 : w >= 1280 ? 5 : w >= 760 ? 3 : w >= 480 ? 2 : 1);
/** 인사이트 카드 열 수 — 3 · 2 · 1(카드 1장 최소 약 270px). 폭 미상이면 3(공용 격자와 같다). */
export const fpCardColumns = (w) => (!w ? 3 : w >= 860 ? 3 : w >= 560 ? 2 : 1);
/** 인사이트 카드 격자 — 공용 STATUS_CARD_GRID_SX 의 3열 고정을 폭에 맞춘 열 수로 바꾼다. */
export const fpCardGridSx = (columns = 3) => ({
  ...STATUS_CARD_GRID_SX, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
});

// ── KPI 카드 ──────────────────────────────────────────────────────────────────
/** 현황판 카드 5장. 나머지 축은 카드 보조 줄로 접는다 — 9장을 같은 크기로 늘어놓으면 무엇이 중요한지 안 보인다. */
export const FP_STATUS_HEADLINE = ["a", "b", "c", "d", "e"];
/** 보조 줄로 접히는 축 — g·i 는 d(리드타임)의 분해, f 는 e(교체 시간)의 짝이다. */
export const FP_STATUS_FOLDED = ["g", "i", "f"];

/** 선택된 비교 블록 — 없거나 모르는 종류면 null. */
export function fpPickCompare(data, kind) {
  const opts = data?.kpi_compare?.options ?? {};
  return (kind && opts[kind]) || null;
}

/**
 * 현황판 KPI 카드 props — **순수 함수**(렌더 테스트 없이 계약을 고정할 수 있게).
 *
 * @param {object} data       initial-state 응답
 * @param {Function} t        번역 함수
 * @param {string|null} kind  선택된 비교 기준("previous" | "confirmed" | null)
 * @param {object} labelOf    {축: 라벨 키} — FP_MODULE.kpiLabels 에서 만든다(순환 import 회피)
 *
 * 🔴 값 유무는 `kpi_current`(원시)로 가른다 — `kpi_current_display` 는 값이 없으면 "—" 문자열이라
 *    그대로 넘기면 StatusKpiCard 가 실측값으로 오인해 상태색을 칠한다. **0 은 실측값**이다.
 * 🔴 미지 축은 조용히 버리지 않는다 — 카드로 뒤에 붙인다(라벨이 없으면 키 원문).
 * 🔵 h(추가 투입 능력)는 이 경로에서 늘 결측이다(기준 버전이 있어야 정의된다) — 값이 올 때만 그린다.
 */
export function buildFpStatusKpiCards(data, t, kind, labelOf = {}) {
  const keys = Array.isArray(data?.kpi_keys) && data.kpi_keys.length ? data.kpi_keys : FP_STATUS_HEADLINE;
  const raw = data?.kpi_current ?? {};
  const display = data?.kpi_current_display ?? {};
  const displayI18n = data?.kpi_current_display_i18n ?? {};
  const state = data?.current_state ?? {};
  const board = data?.status_board ?? null;
  const cmp = fpPickCompare(data, kind);
  const tone = (kind && data?.kpi_tone_by_compare?.[kind]) || data?.kpi_tone || {};
  const show = (k) => raw?.[k] == null ? KPI_EMPTY : (cellText(t, displayI18n[k], display?.[k]) || String(raw[k]));
  // 🔵 값과 단위를 나눠 넘긴다(DP·MP 카드와 같은 계약) — 한 문자열 「124,410 min」 은 좁은 카드에서 공백에서
  //    꺾여 두 줄이 됐다(캡처 170). 조각은 **서버 번역 셀**(값 + 단위 키)에서 그대로 떼어 온다 — 그 셀은 단위가
  //    ko 로 새지 않게 하는 계약이다([머지 점검 GAP 5]). 셀이 없는 축(비율 등)은 종전대로 표시 문자열 통째다.
  const valueParts = (k) => {
    if (raw?.[k] == null) return { value: null };
    const cell = displayI18n[k];
    if (cell?.key === VALUE_UNIT_KEY && cell.args?.value != null && cell.args?.unitKey) {
      return { value: String(cell.args.value), unit: t(cell.args.unitKey) };
    }
    return { value: show(k) };
  };

  const footerOf = (k) => {
    // 🔵 건수는 천 단위 구분을 넣은 **문자열**로 넘긴다(캡처 168 — 카드만 「2299건」 이고 납기 막대는 「2,299건」).
    //    문자열 count 는 i18next 복수형 해석을 건너뛰는데, 이 키들은 복수형 변형이 없어 잃는 것이 없다.
    const cnt = (n, key) => (n == null ? null : t(key, { count: fpFmt(n, 0) }));
    if (k === "a") {
      // [D-111] 계획 실패 — FP 현황 전용 키(기존 fpStatusShortFooter 「결품」 은 값을 바꾸지 않는다 · 추가 전용)
      return [cnt(state.order_count, "fpStatusOrderFooter"), cnt(state.short_count, "fpStatusPlanFailFooter")]
        .filter(Boolean).join(" · ") || undefined;
    }
    // 🔴 현황판의 **배타 지연**(결품 아님)을 쓴다 — 아래 납기 막대·지연 신호와 같은 숫자.
    //    폴백 current_state.late_count 도 이제 같은 정의다(캡처 178~180 이후 원천 SQL 을 배타로 바꿨다 —
    //    그 전에는 결품과 겹쳐 현황판이 빠진 경로에서만 540 vs 476 으로 갈렸다).
    if (k === "b") return cnt(board?.delivery?.late_count ?? state.late_count, "fpStatusLateFooter") ?? undefined;
    if (k === "c") {
      const res = cnt(state.resource_count, "fpStatusResourceCountFooter");   // [D-114] en 복수형
      const sat = board?.resource_summary?.saturated_count;
      return [res, sat ? t("fpStatusBottleneckFooter", { count: sat }) : null].filter(Boolean).join(" · ") || undefined;
    }
    if (k === "d") {
      if (raw.g == null && raw.i == null) return undefined;
      return t("fpStatusFlowSplitFooter", { g: fpAxisValueText(t, "g", raw.g), i: fpAxisValueText(t, "i", raw.i) });
    }
    if (k === "e") return raw.f == null ? undefined : t("fpStatusJcCountFooter", { value: fpAxisValueText(t, "f", raw.f) });
    return undefined;
  };

  const cards = [];
  keys.forEach((k) => {
    if (FP_STATUS_FOLDED.includes(k)) return;
    if (k === "h" && raw?.[k] == null) return;
    const delta = cmp?.delta?.[k];
    const dir = cmp?.direction?.[k];
    const dTone = cmp?.delta_tone?.[k] ?? "neutral";
    const baseV = cmp?.base?.[k];
    const excluded = !!cmp?.dropped?.includes(k);
    const vp = valueParts(k);
    cards.push({
      key: k,
      label: t(labelOf[k] ?? k),
      value: vp.value,
      unit: vp.unit,
      color: FP_TONE_COLOR[tone[k]] ?? FP_TONE_COLOR.neutral,
      footer: footerOf(k),
      // 비교 기준이 있으면 모든 카드에 둘째 줄을 둔다(빈 줄 포함) — 카드 높이가 들쭉날쭉하지 않게.
      //   🔵 표본 하한으로 **비교에서 뺀 축**은 빈 줄이 아니라 「비교 제외」 라고 쓴다 — 빈칸이면 왜 배지가
      //      없는지 알 수 없다(캡처 166: 확정 계획 대비 리드타임·교체 카드).
      footer2: cmp
        ? (excluded ? t("fpStatusCmpExcludedFooter")
          : baseV == null ? "" : t("fpStatusCmpBaseFooter", { value: fpAxisValueText(t, k, baseV) }))
        : undefined,
      valueRight: cmp && delta != null ? (
        <DeltaBadge text={dir === "same" ? t("fpStatusDeltaSame") : fpDeltaText(t, k, delta)}
                    arrow={dir === "same" || dir === "none" ? "" : delta > 0 ? "▲" : "▼"}
                    label={dir === "better" ? t("fpAiDirImproved") : dir === "worse" ? t("fpAiDirWorsened") : ""}
                    color={DELTA_TONE_COLOR[dTone] ?? DELTA_TONE_COLOR.neutral} />
      ) : undefined,
    });
  });
  return cards;
}

const KPI_EMPTY = "—";
/** 서버 KPI 번역 셀 키 — `{ value, unitKey }` 를 받아 「값 + 단위」 로 조립한다(scenario/fp FP_KPI_META). */
const VALUE_UNIT_KEY = "fpAiValueUnit";

function DeltaBadge({ text, arrow, label, color }) {
  // 수치·단위·화살표는 줄바꿈 없는 공백으로 한 덩어리 — 「개선/악화」 앞에서만 줄을 바꾼다.
  //   종전에는 좁은 카드에서 「−210 min / ▼ / Improved」 세 줄로 쪼개져 화살표가 홀로 한 줄을 차지했다(캡처 174).
  //   🔵 [D-115] 한자는 공백이 아니라 **글자 사이**에서도 줄을 바꾼다 — NBSP 로는 「+240分鐘」 · 「惡化」 가
  //      「惡 / 化」 로 끊겼다(캡처 195·196). 두 덩어리를 각각 nowrap span 으로 묶어 둘 사이에서만 꺾는다.
  const head = `${text}${arrow ? ` ${arrow}` : ""}`.replace(/ /g, " ");
  return (
    <Typography component="span"
      // 바깥은 nowrap 을 걸지 않는다 — 좁은 화면에서 카드(overflow:hidden)가 「개선/악화」 를 잘라 먹는 대신 줄을 바꾼다.
      sx={{ fontSize: FONT.BODY, fontWeight: 700, color, lineHeight: 1.4 }}>
      <span style={{ whiteSpace: "nowrap" }}>{head}</span>
      {label ? <>{" "}<span style={{ whiteSpace: "nowrap" }}>{label}</span></> : null}
    </Typography>
  );
}

/**
 * [D-115] 공용 `StatusKpiCard` 로 넘기기 직전 단위를 nowrap 노드로 감싼다 — 한자 단위(「分鐘」)가 좁은 카드에서
 * 「分 / 鐘」 으로 세로로 쪼개졌다(캡처 195·196). 공용 카드는 단위 노드를 그대로 그린다(`typeof unit === "string"`
 * 분기). `buildFpStatusKpiCards` 의 unit 은 문자열로 둔다(값 계약 · 테스트).
 */
export const fpKpiCardsForRender = (cards) => (cards ?? []).map((c) => (typeof c.unit === "string" && c.unit.trim()
  ? { ...c, unit: <span style={{ whiteSpace: "nowrap" }}>{c.unit.trim()}</span> } : c));

// ── 비교 기준 토글 ────────────────────────────────────────────────────────────
/** KPI 섹션 상단(그리드 위) 한 줄 — 비교 기준 선택 + 기준 버전 · 색 기준 안내. */
export function FpCompareBar({ data, kind, onKindChange }) {
  const { t } = useTranslation();
  const opts = data?.kpi_compare?.options ?? {};
  const kinds = ["previous", "confirmed"].filter((k) => opts[k]);
  const cmp = fpPickCompare(data, kind);
  const tip = (
    <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{t("fpStatusToneBasisTip")}</Box>} placement="top-start" arrow>
      <InfoOutlinedIcon sx={{ fontSize: 14, color: UIC.text3, cursor: "help" }} />
    </Tooltip>
  );
  if (!kinds.length) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
        <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3 }}>{t("fpStatusCmpNone")}</Typography>
        {tip}
      </Box>
    );
  }
  const labelKey = { previous: "fpStatusCmpPrevious", confirmed: "fpStatusCmpConfirmed" };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: UIC.text2 }}>{t("fpStatusCmpLabel")}</Typography>
      <AiSegmentedControl value={kind} onChange={onKindChange}
        options={kinds.map((k) => ({ value: k, label: t(labelKey[k]) }))} />
      {cmp && (
        <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3, fontVariantNumeric: "tabular-nums" }}>
          {cmp.version_cd}{cmp.plan_dt ? ` · ${String(cmp.plan_dt).slice(0, 10)}` : ""}
        </Typography>
      )}
      {tip}
      {cmp?.dropped?.length > 0 && (
        <Typography sx={{ fontSize: FONT.BODY, color: UIC.g2t }}>
          {t("fpStatusCmpDropped", { pct: fpFmt((cmp.pop_ratio ?? 0) * 100, 0) })}
        </Typography>
      )}
      {cmp && !cmp.dropped?.length && (
        <Typography sx={{ fontSize: FONT.CAPTION, color: UIC.text3 }}>{t("fpStatusCmpPairNote")}</Typography>
      )}
    </Box>
  );
}

// ── 분포 막대(색 지정형) ──────────────────────────────────────────────────────
// 공용 StatusDistributionBar 는 **한 색의 명암 램프**라 준수(초록)·지연(주황)·결품(빨강)처럼 의미가 다른
// 색을 줄 수 없다. 공용을 고치지 않고 같은 치수(막대 15px · 세그 간격 2px · 하한 3px)로 FP 판을 둔다.
const SPLIT_BAR_H = 15;
const SPLIT_GAP = "2px";
const SPLIT_MIN_W = 3;

export function FpSplitBar({ title, subtitle, segments = [], formatValue = (v) => fpFmt(v, 0), unit = "", emptyText }) {
  const total = segments.reduce((s, x) => s + Math.max(Number(x.value) || 0, 0), 0);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8, mb: 0.8 }}>
        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: UIC.text1 }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3 }}>{subtitle}</Typography>}
      </Box>
      {total <= 0 ? <StatusEmpty text={emptyText} /> : (
        <>
          <Box sx={{ display: "flex", gap: SPLIT_GAP }}>
            {segments.filter((s) => Number(s.value) > 0).map((s) => (
              <Tooltip key={s.key} {...BAR_TIP_PROPS}
                title={<BarTip rows={[{ color: s.color, label: s.label,
                  value: `${formatValue(s.value)}${unit} · ${fpFix(pctOf(s.value, total), 1)}%` }]} />}>
                <Box sx={{ flexGrow: Number(s.value), flexBasis: 0, minWidth: `${SPLIT_MIN_W}px`,
                           height: SPLIT_BAR_H, borderRadius: "2px", bgcolor: s.color }} />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.4, mt: 0.8 }}>
            {segments.map((s) => (
              <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: s.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: FONT.BODY, color: UIC.text2 }}>{s.label}</Typography>
                <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: UIC.text1, fontVariantNumeric: "tabular-nums" }}>
                  {formatValue(s.value)}{unit}
                </Typography>
                <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3, fontVariantNumeric: "tabular-nums" }}>
                  {fpFix(pctOf(Number(s.value) || 0, total), 1)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

/** KPI 섹션 아래 — 납기 현황 · 리드타임 구성 2열. 현황판이 없으면(구 서버) null. */
export function FpKpiBreakdown({ data }) {
  const { t } = useTranslation();
  const dv = data?.status_board?.delivery;
  if (!dv) return null;
  const raw = data?.kpi_current ?? {};
  const dayUnit = t("fpAiUnitDay");
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
      <FpSplitBar
        title={t("fpStatusDeliveryTitle")}
        subtitle={t("fpStatusDeliverySub", { count: fpFmt(dv.order_count, 0) })}
        unit={t("caseUnitLabel")}
        emptyText={t("fpStatusDeliveryEmpty")}
        segments={[
          // [D-111] 세 분류 모두 FP 현황 전용 키 — 기존 코드 라벨은 언어마다 영어(vi)·번체(zh-CN)가 섞였다
          { key: "ontime", label: t("fpStatusOntime"), value: dv.ontime_count, color: DELIVERY_COLOR.ontime },
          { key: "late", label: t("fpStatusLate"), value: dv.late_count, color: DELIVERY_COLOR.late },
          { key: "short", label: t("fpStatusPlanFail"), value: dv.short_count, color: DELIVERY_COLOR.short },
        ]}
      />
      <FpSplitBar
        title={t("fpStatusLeadTimeTitle")}
        subtitle={raw.d == null ? undefined
          : t("fpStatusLeadTimeSub", { value: fpAxisValueText(t, "d", raw.d) })}
        unit={dayUnit}
        // 고정 1자리 — 「25.1일」 옆에 「41일」 이 서지 않게(캡처 168)
        formatValue={(v) => fpFix(v, 1)}
        emptyText={t("fpStatusLeadTimeEmpty")}
        segments={[
          { key: "g", label: t("fpKpiInstageDwell"), value: raw.g, color: FLOW_COLOR.g },
          { key: "i", label: t("fpKpiInterstageWait"), value: raw.i, color: FLOW_COLOR.i },
        ]}
      />
    </Box>
  );
}

// ── 주요 이슈(서버 신호) ──────────────────────────────────────────────────────
const FIXED_UNIT_DIGITS = { percentUnit: 1, fpAiUnitDay: 1 };
function metricText(t, sig, m) {
  if (m.value == null) return "—";
  if (sig.code === "worsened") return fpDeltaText(t, m.axis, m.value) ?? "—";
  // 비율·일수는 고정 1자리 — 같은 카드에 「100%」 와 「20.9%」 가 나란히 서지 않게. 건수·대수는 정수.
  const unitDigits = FIXED_UNIT_DIGITS[m.unit_key];
  const text = unitDigits != null ? fpFix(m.value, unitDigits) : fpFmt(m.value, Number.isInteger(m.value) ? 0 : 1);
  return `${text}${m.unit_key ? t(m.unit_key) : ""}`;
}

/**
 * 서버 인자 중 **숫자만** 표시 형식으로 바꾼다(천 단위 구분 · 정수는 소수 없이). 문자열(버전 코드 · 이름)은 그대로.
 *   🔵 캡처 165 — 제목이 「납기 지연 2299건」 으로 떠 지표 칸(2,299건)과 표기가 갈렸다.
 */
export function fpFmtArgs(args) {
  return Object.fromEntries(Object.entries(args ?? {}).map(([k, v]) => (
    [k, typeof v === "number" ? fpFmt(v, Number.isInteger(v) ? 0 : 1) : v])));
}

/** 서버 신호(status_signals) → 인사이트 카드. 문장은 전부 서버 키 + 인자로 만든다([D-089] — 한국어 결정형 금지). */
export function FpSignalCards({ signals, columns = 3 }) {
  const { t } = useTranslation();
  const list = [...(signals ?? [])].sort(
    (a, b) => (FP_SEVERITY_ORDER[a.severity] ?? 9) - (FP_SEVERITY_ORDER[b.severity] ?? 9));
  return (
    <Box sx={fpCardGridSx(columns)}>
      {list.map((s, i) => {
        const sev = FP_SEVERITY[s.severity] ?? FP_SEVERITY.LOW;
        return (
          <StatusInsightCard key={`${s.code}-${i}`} accent={{ color: sev.color, text: sev.text }}
            eyebrow={t(sev.labelKey)} title={t(s.title_key, fpFmtArgs(s.title_args))}>
            {s.metrics?.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.6 }}>
                {s.metrics.map((m, k) => (
                  <StatusCardField key={k} label={fpLabel(t, m.label_key)}>
                    <Typography sx={{ fontSize: FONT.H2, fontWeight: 700, color: k === 0 ? sev.color : UIC.text1,
                                      fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
                      {metricText(t, s, m)}
                    </Typography>
                  </StatusCardField>
                ))}
              </Box>
            )}
            {s.targets?.length > 0 && (
              <StatusCardField label={t("fpSigTargetLabel")}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {s.targets.map((tg) => (
                    // 긴 이름은 칩 안에서 말줄임 — 이름만 줄이고 건수·비율은 남긴다(MP 대상 칩과 같은 방식).
                    <Box key={tg.cd} sx={{ ...STATUS_TARGET_CHIP_SX, display: "inline-flex", alignItems: "center",
                                           gap: 0.5, maxWidth: "100%" }}>
                      {/* 하한 3em — 좁은 카드에서 이름이 0폭으로 사라지고 「· 92.9%」 만 남았다(캡처 173) */}
                      <Typography noWrap title={tg.nm} sx={{ fontSize: FONT.BODY, color: UIC.text1, minWidth: "3em" }}>
                        {tg.nm}
                      </Typography>
                      {(tg.count != null || tg.pct != null) && (
                        <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3, flexShrink: 0, whiteSpace: "nowrap" }}>
                          {tg.count != null ? `· ${fpFmt(tg.count, 0)}${t("caseUnitLabel")}` : `· ${fpFix(tg.pct, 1)}%`}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </StatusCardField>
            )}
            {/* [D-111] 사유 — 엔진이 plan_problem 에 기록한 사유(능력·자재 등)별 작업주문 수. 계획 실패·지연 카드만 온다.
                  표시 전용이다(판정에 쓰지 않는다 · 한 작업주문이 여러 사유에 잡힐 수 있다). */}
            {s.reasons?.length > 0 && (
              <StatusCardField label={t("reason")}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {s.reasons.map((r) => (
                    <Box key={r.label_key} sx={{ ...STATUS_TARGET_CHIP_SX, display: "inline-flex", alignItems: "center",
                                                 gap: 0.5, maxWidth: "100%" }}>
                      <Typography noWrap sx={{ fontSize: FONT.BODY, color: UIC.text1, minWidth: "3em" }}>
                        {fpLabel(t, r.label_key)}
                      </Typography>
                      <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3, flexShrink: 0, whiteSpace: "nowrap" }}>
                        {`· ${fpFmt(r.count, 0)}${t("caseUnitLabel")}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </StatusCardField>
            )}
            {s.desc_key && (
              <Typography sx={{ fontSize: FONT.BODY, color: UIC.text2, lineHeight: 1.5 }}>
                {t(s.desc_key, fpFmtArgs(s.desc_args))}
              </Typography>
            )}
            {s.basis_key && (
              <Typography sx={{ fontSize: FONT.CAPTION, color: UIC.text3, lineHeight: 1.4 }}>
                {t("fpSigBasisLabel")} · {t(s.basis_key, fpFmtArgs(s.basis_args))}
              </Typography>
            )}
          </StatusInsightCard>
        );
      })}
    </Box>
  );
}

// ── 문제 항목 TOP 5 ───────────────────────────────────────────────────────────
const TOP_N = 5;
const SAT_MARK = 50;   // 병목 기준선 — 서버 SATURATED_HALF(0.5) 와 같은 값의 **표시**(판정은 서버 `saturated`)

/**
 * 문제 유형 코드 → 화면 라벨. [D-111] 계획 실패(`FP_DELIVERY_SHORT` = `shtg_yn='Y'`)·납기 지연(`FP_DELIVERY_DELAY`)은
 * FP 현황 전용 키를 쓴다 — 기존 코드 라벨은 같은 개념을 언어마다 다르게 불렀다(zh 短交·遲交 · vi 영어). 나머지 코드는 그대로.
 */
//  [D-114] 기존 라벨 키(zh-CN 번체 · vi 영어 · en 대소문자)도 FP 전용 새 키로 바꿔 그린다 — 서버가 내는 옛 키
//     (신호 지표 label_key · 사유 코드)도 여기서 바뀐다. 기존 키 값은 다른 FP 화면이 쓰므로 그대로 둔다.
export const FP_LABEL_KEY = {
  FP_DELIVERY_SHORT: "fpStatusPlanFail", FP_DELIVERY_DELAY: "fpStatusLate",
  FP_PLAN_FULFILLMENT_RATE: "fpKpiFulfillRate", FP_ONTIME_PLAN_RATE: "fpKpiPlanOntimeRate",
  FP_AVG_RESRC_UTILIZATION: "fpKpiAvgUtil", FP_AVG_INSTAGE_DWELL: "fpKpiInstageDwell",
  FP_AVG_INTERSTAGE_WAIT: "fpKpiInterstageWait", FP_AVG_PRDT_LEAD_TIME: "fpKpiLeadTime",
  fpKpiJcTimeLabel: "fpKpiJobChangeTime", FP_EXTRA_CAPACITY_EFFORT: "fpKpiExtraCapacity",
  PROBLEM_REASON_BAD_PST: "fpRsnBadPst", PROBLEM_REASON_CAPACITY: "fpRsnCapacity",
  PROBLEM_REASON_INFEASIBLE_DUE: "fpRsnInfeasibleDue", PROBLEM_REASON_MATERIAL: "fpRsnMaterial",
  PROBLEM_REASON_MATERIAL_CAPACITY: "fpRsnMaterialCapacity", PROBLEM_REASON_MISSING_BOR: "fpRsnMissingBor",
  PROBLEM_REASON_NO_PROVIDER: "fpRsnNoProvider", PROBLEM_REASON_TOOL: "fpRsnTool",
};
export const fpLabel = (t, key) => t(FP_LABEL_KEY[key] ?? key);
/** 문제 유형·사유 코드 → 화면 라벨(= fpLabel · 옛 이름 유지). */
export const fpProblemTpLabel = fpLabel;

/** 문제 유형 칩 — TOP 목록의 「병목」·「납기 지연」 칩과 문제 원인 분해 행이 같은 모양을 쓴다. */
export const fpChip = (label, color) => (
  <Chip size="small" label={label}
    sx={{ height: 20, fontSize: FONT.CAPTION, fontWeight: 700, color, bgcolor: "transparent",
          border: `1px solid ${color}55`, "& .MuiChip-label": { px: "7px" } }} />
);

/**
 * 자원 목록 정렬 — 포화 순 / 가동률 순.
 * 🔴 포화 순은 **포화 비율**(화면 열 · 병목 판정 · 신호 카드 대상과 같은 값) 순이다. 종전에는 서버 순서
 *    (포화 **버킷 수**)를 그대로 써서 가동일이 많은 자원(57/61 = 93.4%)이 96.2%(51/53) 위에 섰고, 신호 카드가
 *    짚은 94.3% 자원이 TOP 5 에서 빠졌다(캡처 178·179 · beta). 동률은 포화 버킷 수 → 가동률 순.
 */
export function fpSortResources(resources, mode) {
  const list = [...(resources ?? [])];
  const num = (v) => (v == null || Number.isNaN(Number(v)) ? -1 : Number(v));
  if (mode === "util") {
    list.sort((a, b) => num(b.used_rate) - num(a.used_rate));
  } else {
    list.sort((a, b) => num(b.saturation_pct) - num(a.saturation_pct)
      || num(b.full_bucket_cnt) - num(a.full_bucket_cnt)
      || num(b.used_rate) - num(a.used_rate));
  }
  return list;
}

/** 품목 목록 필터 — 전체(서버 순서 = 문제 많은 순) / 지연 / 결품. */
export function fpFilterItems(items, mode) {
  const list = [...(items ?? [])];
  if (mode === "late") return list.filter((r) => r.late_count > 0).sort((a, b) => b.late_count - a.late_count);
  if (mode === "short") return list.filter((r) => r.short_count > 0).sort((a, b) => b.short_count - a.short_count);
  return list.filter((r) => r.late_count > 0 || r.short_count > 0);
}

function SubHeading({ title, sub, right }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.8, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: UIC.text1 }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3 }}>{sub}</Typography>}
      {right && <Box sx={{ ml: "auto" }}>{right}</Box>}
    </Box>
  );
}

function FpResourceTop({ resources }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("saturation");
  const rows = fpSortResources(resources, mode).slice(0, TOP_N);
  return (
    <Box sx={{ minWidth: 0 }}>
      <SubHeading title={t("fpStatusResTopTitle")} sub={t("fpStatusResTopSub")}
        right={<AiSegmentedControl value={mode} onChange={setMode}
          options={[{ value: "saturation", label: t("fpStatusSortSaturation") },
                    { value: "util", label: t("fpStatusSortUtil") }]} />} />
      {rows.length === 0 ? <StatusEmpty variant="rows" text={t("fpStatusResEmpty")} /> : (
        <StatusItemList>
          <StatusItemHeader nameLabel={t("fpStatusResNameCol")}
            barScale={{ max: 100, marks: [{ at: SAT_MARK, label: `${SAT_MARK}%`, title: t("fpStatusBottleneckMark", { pct: SAT_MARK }) }] }}
            barValue={{ w: 52, label: t("fpStatusSaturationCol"), unit: "%" }}
            cells={[{ w: 52, label: t("fpStatusUtilCol"), unit: "%" }]}
            rightW={64} />
          <Stack spacing={0.7}>
            {rows.map((r) => {
              const accent = r.saturated ? UIC.g3 : UIC.acc;
              return (
                <StatusItemRow key={r.cd} accent={accent}
                  name={<Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: UIC.text1, whiteSpace: "nowrap",
                                          overflow: "hidden", textOverflow: "ellipsis" }}>{r.nm}</Typography>}
                  sub={r.stage_nm}
                  bar={{ value: r.saturation_pct ?? 0, color: accent, max: 100, marker: SAT_MARK,
                         markerLabel: t("fpStatusBottleneckMark", { pct: SAT_MARK }),
                         ariaLabel: `${r.nm} ${fpFix(r.saturation_pct, 1)}%`,
                         tip: <BarTip title={r.nm} rows={[
                           { color: accent, label: t("fpStatusSaturationCol"),
                             value: `${fpFmt(r.full_bucket_cnt, 0)} / ${fpFmt(r.work_bucket_cnt, 0)}${t("fpAiUnitDay")}` },
                           { label: t("fpStatusUtilCol"), value: `${fpFix(r.used_rate, 1)}%` },
                         ]} /> }}
                  // 고정 1자리 — 같은 열에 「68」 과 「92.7」 이 섞이지 않게(캡처 169)
                  barValue={{ w: 52, num: fpFix(r.saturation_pct, 1), unit: "%" }}
                  cells={[{ w: 52, num: fpFix(r.used_rate, 1), unit: "%" }]}
                  // 슬롯 배열로 준다 — null 이면 칩 열 자체가 사라져 그 행만 막대·수치가 밀린다(RP 와 같은 규약).
                  right={[r.saturated ? fpChip(t("fpStatusBottleneckChip"), UIC.g3) : null]}
                  rightW={64} />
              );
            })}
          </Stack>
        </StatusItemList>
      )}
    </Box>
  );
}

/** 준수·지연·결품 누적 막대 — 행 바 열(유연폭)에 들어가는 커스텀 노드(MP 품목 행과 같은 방식). */
function DeliveryStackBar({ row, t }) {
  const total = row.order_count || 0;
  const segs = [
    { k: "ontime", v: row.ontime_count, c: DELIVERY_COLOR.ontime, l: t("fpStatusOntime") },
    { k: "late", v: row.late_count, c: DELIVERY_COLOR.late, l: t("fpStatusLate") },
    { k: "short", v: row.short_count, c: DELIVERY_COLOR.short, l: t("fpStatusPlanFail") },
  ];
  return (
    <Box sx={STATUS_BAR_COL_SX}>
      <Tooltip {...BAR_TIP_PROPS} title={<BarTip title={row.item_nm} rows={segs.map((s) => (
        { color: s.c, label: s.l, value: `${fpFmt(s.v, 0)}${t("caseUnitLabel")}` }))} />}>
        <Box sx={{ display: "flex", height: STATUS_BAR_H, borderRadius: STATUS_BAR_RADIUS, overflow: "hidden",
                   bgcolor: STATUS_BAR_TRACK }}>
          {total > 0 && segs.filter((s) => s.v > 0).map((s) => (
            <Box key={s.k} sx={{ width: `${(s.v / total) * 100}%`, minWidth: "3px", bgcolor: s.c }} />
          ))}
        </Box>
      </Tooltip>
    </Box>
  );
}

function FpItemTop({ items, itemTotal }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("all");
  const rows = fpFilterItems(items, mode).slice(0, TOP_N);
  return (
    <Box sx={{ minWidth: 0 }}>
      <SubHeading title={t("fpStatusItemTopTitle")} sub={t("fpStatusItemTopSub")}
        right={<AiSegmentedControl value={mode} onChange={setMode}
          options={[{ value: "all", label: t("all") }, { value: "late", label: t("fpStatusFilterLate") },
                    { value: "short", label: t("fpStatusFilterShort") }]} />} />
      {rows.length === 0 ? <StatusEmpty variant="rows" text={t("fpStatusItemEmpty")} /> : (
        <StatusItemList>
          <StatusItemHeader nameLabel={t("fpStatusItemNameCol")}
            barValue={{ w: 52, label: t("fpStatusOtdCol"), unit: "%" }}
            cells={[{ w: 96, label: t("fpStatusLateShortCol") }]}
            rightW={72} />
          <Stack spacing={0.7}>
            {rows.map((r) => {
              const otd = pctOf(r.ontime_count, r.order_count);
              const accent = r.short_count > 0 ? UIC.g3 : r.late_count > 0 ? UIC.g2 : UIC.g1;
              return (
                <StatusItemRow key={`${r.item_cd}@${r.plant_cd}`} accent={accent}
                  name={<Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: UIC.text1, whiteSpace: "nowrap",
                                          overflow: "hidden", textOverflow: "ellipsis" }}>{r.item_nm}</Typography>}
                  sub={r.plant_nm || r.plant_cd}
                  bar={<DeliveryStackBar row={r} t={t} />}
                  barValue={{ w: 52, num: fpFix(otd, 1), unit: "%" }}
                  cells={[{ w: 96, node: (
                    <Typography sx={{ fontSize: FONT.BODY, fontVariantNumeric: "tabular-nums", color: UIC.text2 }}>
                      <Box component="span" sx={{ color: r.late_count ? UIC.g2t : UIC.text3, fontWeight: 600 }}>{fpFmt(r.late_count, 0)}</Box>
                      {" · "}
                      <Box component="span" sx={{ color: r.short_count ? UIC.g3 : UIC.text3, fontWeight: 600 }}>{fpFmt(r.short_count, 0)}</Box>
                    </Typography>) }]}
                  right={r.short_count > 0 ? fpChip(t("fpStatusPlanFail"), UIC.g3)
                    : r.late_count > 0 ? fpChip(t("fpStatusLate"), UIC.g2) : null}
                  rightW={72} />
              );
            })}
          </Stack>
        </StatusItemList>
      )}
      {itemTotal > 0 && (
        <Typography sx={{ mt: 0.6, fontSize: FONT.CAPTION, color: UIC.text3, textAlign: "right" }}>
          {t("fpStatusItemOfTotal", { shown: rows.length, count: fpFmt(itemTotal, 0) })}
        </Typography>
      )}
    </Box>
  );
}

/**
 * @param {boolean} [twoCol] 자원·품목 2열 여부 — 가용폭으로 호출부가 정한다. 없으면 뷰포트(lg) 기준(구 동작).
 */
export function FpTopProblems({ board, twoCol }) {
  const { t } = useTranslation();
  if (!board) return null;
  const cols = twoCol == null
    ? { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" }
    : twoCol ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)";
  return (
    <StatusSection title={t("topProblemItemsTitle")} subtitle={t("fpStatusTopSubtitle")} collapsible>
      {/* minmax(0, 1fr) — `1fr` 는 min-content 아래로 줄지 않아 긴 이름·막대가 페이지 가로 넘침을 만들 수 있다 */}
      <Box sx={{ display: "grid", gridTemplateColumns: cols, gap: 2.5, alignItems: "start" }}>
        <FpResourceTop resources={board.resources} />
        <FpItemTop items={board.items} itemTotal={board.item_total} />
      </Box>
    </StatusSection>
  );
}

// ── 기간별 자원 부하 추이 ─────────────────────────────────────────────────────
/** 날짜(yyyy-MM-dd) → 묶음 키. 주 = ISO 월요일(UTC 산술 — 시간대에 흔들리지 않게), 월 = yyyy-MM. */
export function fpBucketKey(date, grain) {
  const s = String(date).slice(0, 10);
  if (grain === "month") return s.slice(0, 7);
  if (grain !== "week") return s;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = (dt.getUTCDay() + 6) % 7;          // 월=0 … 일=6
  dt.setUTCDate(dt.getUTCDate() - dow);
  return dt.toISOString().slice(0, 10);
}

/**
 * 열 형식 부하(`{dates[], stages:{cd:{used_h[], avail_h[], full_res[]}}}`) → 차트 행.
 * 공정 필터(stageCd="" = 전체) 후 묶음별로 합한다.
 *   load_h = 부하 · remain_h = max(가용 − 부하, 0) · rate = 부하 ÷ 가용 × 100
 *   🔵 서버가 열 형식으로 보낸다 — 행 형식(일 × 공정)은 공정 이름이 반복돼 1년 계획에서 약 250KB 였다.
 *   🔵 부하가 가용을 넘는 버킷은 엔진이 만들지 않는다(잔여 능력 = max(0, …)) — 그래도 음수를 그리지 않게 접는다.
 */
export function fpRollupLoad(trend, grain, stageCd = "") {
  const dates = trend?.dates ?? [];
  const stages = trend?.stages ?? {};
  const acc = new Map();
  Object.entries(stages).forEach(([cd, s]) => {
    if (stageCd && cd !== stageCd) return;
    dates.forEach((date, i) => {
      const used = s?.used_h?.[i];
      const avail = s?.avail_h?.[i];
      if (used == null || avail == null) return;
      const k = fpBucketKey(date, grain);
      const cur = acc.get(k) ?? { date: k, used: 0, avail: 0, fullRes: 0 };
      cur.used += Number(used) || 0;
      cur.avail += Number(avail) || 0;
      cur.fullRes = Math.max(cur.fullRes, Number(s?.full_res?.[i]) || 0);
      acc.set(k, cur);
    });
  });
  return [...acc.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)).map((b) => ({
    date: b.date,
    load_h: Math.round(b.used * 10) / 10,
    remain_h: Math.round(Math.max(b.avail - b.used, 0) * 10) / 10,
    rate: b.avail > 0 ? Math.round((b.used / b.avail) * 1000) / 10 : null,
    full_res: b.fullRes,
  }));
}

// 가동률 구간 — **표시 눈금**이다(판정 아님). 위에서부터 진한 색.
const UTIL_BANDS = [
  { from: 80, to: null, color: UIC.accT },
  { from: 60, to: 80, color: UIC.acc },
  { from: 40, to: 60, color: UIC.accM },
  { from: 20, to: 40, color: UIC.accS },
  { from: null, to: 20, color: UIC.border },
];

export function fpUtilBands(resources) {
  return UTIL_BANDS.map((b) => ({
    ...b,
    count: (resources ?? []).filter((r) => r.used_rate != null
      && (b.from == null || r.used_rate >= b.from) && (b.to == null || r.used_rate < b.to)).length,
  }));
}

const LOAD_COLORS = { load_h: UIC.accM, remain_h: UIC.g1s };

export function FpLoadTrend({ board }) {
  const { t } = useTranslation();
  const trend = board?.load_trend ?? null;
  const spanDays = trend?.dates?.length ?? 0;
  // 기본 묶음 — 계획 지평이 4개월을 넘으면 주별(1년 계획을 일별 막대 367개로 시작하면 읽히지 않는다).
  const [grain, setGrain] = useState(spanDays > 120 ? "week" : "day");
  const [stage, setStage] = useState("");
  const data = useMemo(() => fpRollupLoad(trend, grain, stage), [trend, grain, stage]);
  // 가동률 구간도 조회 범위(공정)를 따른다 — 같은 섹션의 차트만 공정으로 좁히고 구간은 전 공장이면 어긋난다.
  const scopedRes = useMemo(
    () => (stage ? (board?.resources ?? []).filter((r) => r.stage_cd === stage) : (board?.resources ?? [])),
    [board, stage]);
  const bands = useMemo(() => fpUtilBands(scopedRes), [scopedRes]);
  if (!board || !board.time_unit) return null;
  const peak = data.reduce((m, r) => (r.rate != null && (m == null || r.rate > m.rate) ? r : m), null);
  const sumUsed = data.reduce((s, r) => s + r.load_h, 0);
  const sumAvail = data.reduce((s, r) => s + r.load_h + r.remain_h, 0);
  const stageOpts = [{ value: "", label: t("fpStatusStageAll") },
                     ...(board.stages ?? []).map((s) => ({ value: s.cd, label: s.nm || s.cd }))];
  const toolbar = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
      <AiSegmentedControl value={grain} onChange={setGrain}
        options={[{ value: "day", label: t("daily") }, { value: "week", label: t("trendGrainWeeklyLabel") },
                  { value: "month", label: t("monthly") }]} />
      <StatusToolbar label={t("trendScopeLabel")}>
        <StatusToolbarSelect value={stage} onChange={setStage} options={stageOpts} />
      </StatusToolbar>
    </Box>
  );
  return (
    <StatusSection title={t("fpStatusTrendTitle")} subtitle={t("fpStatusTrendSub")} toolbar={toolbar} collapsible>
      {data.length === 0 ? <StatusEmpty variant="chart" text={t("fpStatusTrendEmpty")} /> : (
        <Stack spacing={1.5}>
          {peak && (
            <Typography sx={{ fontSize: FONT.BODY, color: UIC.text2, fontVariantNumeric: "tabular-nums" }}>
              {t("fpStatusTrendSummary", { avg: fpFix(pctOf(sumUsed, sumAvail), 1), max: fpFix(peak.rate, 1), date: peak.date })}
            </Typography>
          )}
          {/* recharts ResponsiveContainer 는 부모가 min-content 로 버티면 줄어들지 못한다 — 폭을 부모에 맡긴다 */}
          <Box sx={{ minWidth: 0, width: "100%" }}>
            <ForecastActualChart xKey="date" height={220} unit="h"
              trend={{ data, series: [{ key: "load_h", name_key: "fpStatusSeriesLoad" },
                                      { key: "remain_h", name_key: "fpStatusSeriesRemain" }] }}
              barKeys={["load_h", "remain_h"]} barStackId="capa" dashKeys={[]} colorMap={LOAD_COLORS}
              emptyText={t("fpStatusTrendEmpty")} />
          </Box>
          <FpSplitBar
            title={t("fpStatusUtilDistTitle")}
            subtitle={t("fpStatusResourceCountFooter", { count: scopedRes.length })}
            emptyText={t("fpStatusUtilDistEmpty")}
            unit=""
            segments={bands.map((b, i) => ({
              key: `band${i}`,
              label: b.to == null ? t("fpStatusUtilBandTop", { from: b.from })
                : b.from == null ? t("fpStatusUtilBandLow", { to: b.to })
                : t("fpStatusUtilBand", { from: b.from, to: b.to }),
              value: b.count, color: b.color,
            }))}
          />
        </Stack>
      )}
    </StatusSection>
  );
}
